import { FC } from 'react';

import { Controller, useFormContext } from 'react-hook-form-mui';
import { ArrowIcon } from 'assets';
import { Button } from 'view/components';
import * as S from 'view/modals/OnboardingWizard/styled';
import TestSelect from 'view/pages/profile/TestPage/Select';

interface DemographicsStepProps {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  isSaving?: boolean;
}

const genderOptions = [
  { id: 1, name: 'Male' },
  { id: 2, name: 'Female' },
  { id: 3, name: 'Non-Binary' },
  { id: 4, name: 'Prefer not to say' },
  { id: 5, name: 'Other' },
];

const ethnicityOptions = [
  { id: 1, name: 'Asian' },
  { id: 2, name: 'Black, including African American' },
  { id: 3, name: 'Hispanic, Latino/a or Latinx' },
  { id: 4, name: 'Middle Eastern or North African' },
  { id: 5, name: 'Native American or Alaska Native' },
  { id: 6, name: 'Pacific Islander, including Native Hawaiian' },
  { id: 7, name: 'White or Caucasian' },
  { id: 8, name: 'Prefer not to say' },
];

const disabilityStatusOptions = [
  { id: 1, name: 'Yes' },
  { id: 2, name: 'No' },
  { id: 3, name: 'Prefer not to say' },
];

const veteranStatusOptions = [
  { id: 1, name: 'Yes' },
  { id: 2, name: 'No' },
  { id: 3, name: 'Prefer not to say' },
];

const DemographicsStep: FC<DemographicsStepProps> = ({
  onNext,
  onBack,
  onSkip,
  isSaving = false,
}) => {
  const { control } = useFormContext();

  return (
    <S.WizardContent>
      <S.StepHeader>
        <S.StepTitle>Demographic Information</S.StepTitle>
        <S.StepSubtitle>
          We collect demographic information to help us ensure fair and inclusive hiring practices.
          This information is completely confidential, will never be shared with employers, and is
          used only for statistical and reporting purposes to help improve our platform&apos;s
          equity and inclusivity.
        </S.StepSubtitle>
      </S.StepHeader>
      <S.StepFormContainer>
        <Controller
          name="gender"
          control={control}
          render={({ field }) => {
            return (
              <TestSelect
                {...field}
                label="Gender"
                options={genderOptions}
                selectedValue={field.value}
                onChange={(selected) => {
                  return field.onChange(selected);
                }}
              />
            );
          }}
        />
        <Controller
          name="ethnicity"
          control={control}
          render={({ field }) => {
            return (
              <TestSelect
                {...field}
                label="Ethnicity"
                options={ethnicityOptions}
                selectedValue={field.value}
                onChange={(selected) => {
                  return field.onChange(selected);
                }}
              />
            );
          }}
        />
        <Controller
          name="disabilityStatus"
          control={control}
          render={({ field }) => {
            return (
              <TestSelect
                {...field}
                label="Disability Status"
                options={disabilityStatusOptions}
                selectedValue={field.value}
                onChange={(selected) => {
                  return field.onChange(selected);
                }}
              />
            );
          }}
        />
        <Controller
          name="veteranStatus"
          control={control}
          render={({ field }) => {
            return (
              <TestSelect
                {...field}
                label="Veteran Status"
                options={veteranStatusOptions}
                selectedValue={field.value}
                onChange={(selected) => {
                  return field.onChange(selected);
                }}
              />
            );
          }}
        />
      </S.StepFormContainer>
      <S.WizardFooter>
        <Button variant="outlined" text="Back" onClick={onBack} disabled={isSaving} />
        <Button
          variant="contained"
          text={isSaving ? 'Saving...' : 'Next'}
          onClick={onNext}
          disabled={isSaving}
          endIcon={<ArrowIcon rotate="right" color="#FFFFFF" />}
        />
      </S.WizardFooter>
    </S.WizardContent>
  );
};

export default DemographicsStep;
